import { useEffect, useState } from "react";

import { getServices } from "@/utils/services/services-helper";
import {
  TextField,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import { useRouter } from "next/router";

import { useForm } from "react-hook-form";

import Container from "./container";

import FormProvider from "components/formProvider";

// context
import { useBookingContext } from "../hooks/useBooking";
import { BookingContextType, bookingInfo } from "../utils/booking";

// =================================================================

type LocationType = {
  loading: boolean;
  assigned_staffs: any;
  search: string;
  serviceId: string;
};

const AvailableStates = () => {
  const context = useBookingContext() as BookingContextType;
  const [location, setLocation] = useState<LocationType>({
    serviceId: "",
    loading: false,
    assigned_staffs: [],
    search: "",
  });
  const router = useRouter();

  useEffect(() => {
    const { email, name, phone, gender, promo, couponCode } = router.query;
    const fullName = String(name).split(" ");
    const code = promo ? String(promo) : couponCode ? String(couponCode) : "";
    context.setFreeConsult({
      ...bookingInfo.data.freeConsult,
      state: location.search,
      selected_state: location.search,
      first_name: name ? fullName.slice(0, fullName.length - 1).join(" ") : "",
      last_name: name ? fullName[fullName.length - 1] : "",
      email: email ? String(email) : "",
      phone: phone ? String(phone) : "",
      gender: gender ? String(gender) : "",
      city: "",
      zip: "",
      couponCode: code ?? "",
      fromSA: true,
    });
  }, [context, location.search, router.query]);

  useEffect(() => {
    if (
      !location.loading &&
      location.search !== "" &&
      location.assigned_staffs.length === 0
    )
      router.push("/out-of-service");
  }, [
    location.search,
    location.loading,
    location.assigned_staffs.length,
    router,
  ]);

  const methods = useForm();

  const { handleSubmit } = methods;

  const handleSearch = async (search: string) => {
    setLocation((prev) => ({ ...prev, search, loading: true }));
    const serviceId = process.env.ITH_SERVICE_ID!;
    const result = await getServices({
      token: localStorage.getItem("accessToken") ?? "",
      serviceId,
      state: search,
    });
    setLocation((prev) => ({
      ...prev,
      assigned_staffs: result?.["Available Services"]?.[0] ?? [],
      serviceId,
      loading: false,
    }));
  };

  const handleConfirm = () => {
    router.push("/insurance");
  };

  return (
    <Container sx={{ mt: 2, p: 2, width: { xs: "100%", lg: "500px" } }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(handleConfirm)}>
        <TextField
          select
          label="Select your State"
          fullWidth
          size={"small"}
          value={location.search}
          onChange={(e) => handleSearch(e.target.value)}
        >
          <MenuItem value="AL">Alabama</MenuItem>
          <MenuItem value="AK">Alaska</MenuItem>
          <MenuItem value="AZ">Arizona</MenuItem>
          <MenuItem value="AR">Arkansas</MenuItem>
          <MenuItem value="CA">California</MenuItem>
          <MenuItem value="CO">Colorado</MenuItem>
          <MenuItem value="CT">Connecticut</MenuItem>
          <MenuItem value="DE">Delaware</MenuItem>
          <MenuItem value="DC">District Of Columbia</MenuItem>
          <MenuItem value="FL">Florida</MenuItem>
          <MenuItem value="GA">Georgia</MenuItem>
          <MenuItem value="HI">Hawaii</MenuItem>
          <MenuItem value="ID">Idaho</MenuItem>
          <MenuItem value="IL">Illinois</MenuItem>
          <MenuItem value="IN">Indiana</MenuItem>
          <MenuItem value="IA">Iowa</MenuItem>
          <MenuItem value="KS">Kansas</MenuItem>
          <MenuItem value="KY">Kentucky</MenuItem>
          <MenuItem value="LA">Louisiana</MenuItem>
          <MenuItem value="ME">Maine</MenuItem>
          <MenuItem value="MD">Maryland</MenuItem>
          <MenuItem value="MA">Massachusetts</MenuItem>
          <MenuItem value="MI">Michigan</MenuItem>
          <MenuItem value="MN">Minnesota</MenuItem>
          <MenuItem value="MS">Mississippi</MenuItem>
          <MenuItem value="MO">Missouri</MenuItem>
          <MenuItem value="MT">Montana</MenuItem>
          <MenuItem value="NE">Nebraska</MenuItem>
          <MenuItem value="NV">Nevada</MenuItem>
          <MenuItem value="NH">New Hampshire</MenuItem>
          <MenuItem value="NJ">New Jersey</MenuItem>
          <MenuItem value="NM">New Mexico</MenuItem>
          <MenuItem value="NY">New York</MenuItem>
          <MenuItem value="NC">North Carolina</MenuItem>
          <MenuItem value="ND">North Dakota</MenuItem>
          <MenuItem value="OH">Ohio</MenuItem>
          <MenuItem value="OK">Oklahoma</MenuItem>
          <MenuItem value="OR">Oregon</MenuItem>
          <MenuItem value="PA">Pennsylvania</MenuItem>
          <MenuItem value="RI">Rhode Island</MenuItem>
          <MenuItem value="SC">South Carolina</MenuItem>
          <MenuItem value="SD">South Dakota</MenuItem>
          <MenuItem value="TN">Tennessee</MenuItem>
          <MenuItem value="TX">Texas</MenuItem>
          <MenuItem value="UT">Utah</MenuItem>
          <MenuItem value="VT">Vermont</MenuItem>
          <MenuItem value="VA">Virginia</MenuItem>
          <MenuItem value="WA">Washington</MenuItem>
          <MenuItem value="WV">West Virginia</MenuItem>
          <MenuItem value="WI">Wisconsin</MenuItem>
          <MenuItem value="WY">Wyoming</MenuItem>
        </TextField>

        <input type="hidden" id="zc_gad" name="zc_gad" value="" />
        {location.loading && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              my: 1,
              flexDirection: "column",
            }}
          >
            <CircularProgress />
            <Typography variant="caption" component="p" color="primary">
              Checking Availability. Please wait...
            </Typography>
          </Box>
        )}
        {!location.loading && (
          <LoadingButton
            fullWidth
            variant="contained"
            sx={{
              mt: 4,
              color: "#fff",
              textTransform: location.loading ? "capitalize" : "uppercase",
            }}
            disabled={location.loading || location.search === ""}
            onClick={handleConfirm}
          >
            {location?.loading ? "Checking Availability..." : "Confirm"}
          </LoadingButton>
        )}
      </FormProvider>
    </Container>
  );
};

export default AvailableStates;
